import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BaseSlotComponent } from '../base-slot.component';
import { ZardComboboxComponent } from '../../../ui/combobox/combobox.component';
import { ApiService } from 'app/core/api/api.service';
import { IOption } from 'app/shared/shared.types';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { TranslocoPipe } from '@ngneat/transloco';

@Component({
    selector: 'combobox-slot',
    standalone: true,
    templateUrl: './combobox-slot.component.html',
    imports: [ZardComboboxComponent, ReactiveFormsModule, TranslocoPipe],
})
export class ComboboxSlotComponent
    extends BaseSlotComponent
    implements OnInit, OnDestroy
{
    options: IOption[] = [];

    private searchEndpoint?: string;
    private searchField?: string;
    private labelField?: string;
    private valueField?: string;
    private responsePath?: string;

    // Stream of raw search terms from the combobox
    private searchSubject = new Subject<string>();
    private searchSub?: Subscription;

    constructor(private apiService: ApiService) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // Initialize options from slot definition
        this.options = (this.slot.options || []) as IOption[];

        // Read optional remote search config from slot.extra
        const extra: any = this.slot.extra || {};
        this.searchEndpoint = extra.searchEndpoint;
        this.searchField = extra.searchField || this.slot.field;
        this.labelField = extra.labelField || 'label';
        this.valueField = extra.valueField || 'value';
        this.responsePath = extra.responsePath || 'content';

        // Debounce remote search so we don't spam the API
        this.searchSub = this.searchSubject
            .pipe(debounceTime(300)) // 0.5s after user stops typing
            .subscribe((term) => {
                this.performSearch(term);
            });

        if (this.searchEndpoint) {
            this.performSearch('');
        }
    }

    /**
     * Handle search term changes coming from the combobox.
     * If a searchEndpoint is configured, we call it and update options dynamically.
     */
    onSearchChange(term: string): void {
        // If no remote search configured, do nothing
        if (!this.searchEndpoint || !this.searchField) {
            return;
        }

        this.searchSubject.next(term);
    }

    /**
     * Actually perform the HTTP request and update options.
     * This is called in a debounced manner from the searchSubject.
     */
    private performSearch(term: string): void {
        if (!this.searchEndpoint || !this.searchField) {
            return;
        }

        const payload =
            term && term.trim().length > 0
                ? [{ name: this.searchField, value: term.trim() }]
                : [];

        this.apiService.post(this.searchEndpoint, payload).subscribe({
            next: (res: any) => {
                let items: any = res;

                if (this.responsePath) {
                    this.responsePath.split('.').forEach((key) => {
                        if (items && key in items) {
                            items = items[key];
                        } else {
                            items = [];
                        }
                    });
                }

                if (!Array.isArray(items)) {
                    items = [];
                }

                this.options = items
                    .map((item: any) => {
                        const label = item[this.labelField!];
                        const value = item[this.valueField!];
                        if (label == null || value == null) {
                            return null;
                        }
                        return { label, value } as IOption;
                    })
                    .filter((opt: IOption | null): opt is IOption => !!opt);
            },
            error: () => {
                // On error, keep previous options to avoid empty dropdown
            },
        });
    }

    ngOnDestroy(): void {
        if (this.searchSub) {
            this.searchSub.unsubscribe();
            this.searchSub = undefined;
        }
        this.searchSubject.complete();
    }
}

